import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getPortalAccessToken, portalRequest, refreshPortalSession } from "../lib/auth";
import type { PortalItineraryPayload } from "./itinerary.actions";

export type AssistantDraftSummary = {
  title: string;
  startDate: string;
  endDate: string;
  nights: number;
  destinations: string[];
  travellers: { adults: number; cwb: number; cnb: number; infant: number };
  currency: string;
  totalPrice: number;
  perAdult: number;
  hotels: Array<{
    id: string;
    name: string;
    city: string;
    category: string | null;
    roomType: string;
    mealPlan: string;
    checkInDate: string;
    checkOutDate: string;
    nights: number;
    rooms: number;
  }>;
  days: Array<{
    day: number;
    date: string;
    items: Array<{ id: string; type: "activity" | "transport"; name: string; city: string | null }>;
  }>;
  gaps: {
    daysWithoutItems: number[];
    uncoveredHotelNights: number[];
    missingTransfers?: Array<{ day: number; reason: string }>;
  };
  warnings: string[];
};

export type AssistantDraft = {
  guestName: string;
  payload: PortalItineraryPayload;
  summary: AssistantDraftSummary;
};

export type AssistantMessage = {
  role: "user" | "assistant";
  content: string;
  createdAt?: string;
};

export type AssistantThreadSummary = { id: string; title: string | null; updatedAt: string };

export type AssistantThread = {
  id: string;
  title: string | null;
  messages: AssistantMessage[];
  draft: AssistantDraft | null;
};

export type AssistantChatResult = {
  threadId: string;
  title: string | null;
  message: string;
  draft?: AssistantDraft | null;
};

export const assistantKeys = {
  all: ["agent-portal", "assistant"] as const,
  status: () => [...assistantKeys.all, "status"] as const,
  threads: () => [...assistantKeys.all, "threads"] as const,
  thread: (id: string) => [...assistantKeys.all, "thread", id] as const,
};

/**
 * Reports whether the AI assistant can be used by the signed-in agency. `aiEnabled` is false only when AI is
 * switched off for the agency itself; `available` is also false for tenant-level problems such as missing credits.
 */
export const usePortalAiStatusQuery = (enabled = true) =>
  useQuery({
    enabled,
    queryKey: assistantKeys.status(),
    queryFn: async () =>
      (await portalRequest<{ available: boolean; aiEnabled?: boolean; reason?: string }>({ url: "/api/agent-portal/ai/status", method: "get" })).data,
    staleTime: 60_000,
  });

/** Lists the agent's recent AI chats. */
export const usePortalAiThreadsQuery = (enabled = true) =>
  useQuery({
    enabled,
    queryKey: assistantKeys.threads(),
    queryFn: async () =>
      (await portalRequest<{ threads: AssistantThreadSummary[] }>({ url: "/api/agent-portal/ai/threads", method: "get" })).data.threads,
    staleTime: 15_000,
  });

/** Loads one AI chat with its transcript and latest draft. */
export const usePortalAiThreadQuery = (threadId?: string) =>
  useQuery({
    queryKey: assistantKeys.thread(threadId || ""),
    queryFn: async () =>
      (await portalRequest<AssistantThread>({ url: `/api/agent-portal/ai/threads/${threadId}`, method: "get" })).data,
    enabled: Boolean(threadId),
    staleTime: Infinity,
  });

/** Deletes one AI chat. */
export const useDeletePortalAiThreadMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (threadId: string) =>
      portalRequest({ url: `/api/agent-portal/ai/threads/${threadId}`, method: "delete" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assistantKeys.threads() }),
  });
};

export class AssistantChatError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

const openChatStream = (token: string | null, body: string, signal?: AbortSignal) =>
  fetch("/api/agent-portal/ai/chat", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body,
    signal,
  });

/**
 * Sends one message to the AI assistant and reports progress while it works.
 * Resolves with the assistant reply and, when it changed, the latest itinerary draft.
 */
export const sendPortalAiMessage = async (params: {
  message: string;
  threadId?: string;
  signal?: AbortSignal;
  onStatus?: (step: string) => void;
  /** Receives product photos found while the assistant searches inventory. */
  onPreview?: (images: string[]) => void;
}): Promise<AssistantChatResult> => {
  const body = JSON.stringify({ message: params.message, threadId: params.threadId });
  const interrupted = () =>
    new AssistantChatError("The connection to the assistant was interrupted. Your message wasn't lost — try again.", 0);
  let response: Response;
  try {
    response = await openChatStream(getPortalAccessToken(), body, params.signal);
    if (response.status === 401) {
      response = await openChatStream(await refreshPortalSession(), body, params.signal);
    }
  } catch (error) {
    if (params.signal?.aborted) throw error;
    throw interrupted();
  }
  if (!response.ok || !response.body) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw payload?.error ? new AssistantChatError(payload.error, response.status) : interrupted();
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let result: AssistantChatResult | null = null;
  for (;;) {
    let chunk: ReadableStreamReadResult<Uint8Array>;
    try {
      chunk = await reader.read();
    } catch (error) {
      if (params.signal?.aborted) throw error;
      throw interrupted();
    }
    const { done, value } = chunk;
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop() || "";
    for (const raw of events) {
      const line = raw.split("\n").find((entry) => entry.startsWith("data: "));
      if (!line) continue;
      const event = JSON.parse(line.slice(6)) as {
        type: string;
        step?: string;
        error?: string;
        images?: string[];
      } & Partial<AssistantChatResult>;
      if (event.type === "status" && event.step) params.onStatus?.(event.step);
      if (event.type === "preview" && Array.isArray(event.images)) params.onPreview?.(event.images);
      if (event.type === "error") throw new AssistantChatError(event.error || "The AI assistant ran into a problem.", 500);
      if (event.type === "complete") result = event as AssistantChatResult;
    }
  }
  if (!result) throw interrupted();
  return result;
};
